import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  App as AntApp,
  Table,
  Typography,
  Tag,
  Tooltip,
  Button,
  Empty,
  Grid,
  Dropdown,
  Flex,
  Modal,
  Spin,
} from "antd";
import {
  CheckCircleTwoTone,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  EyeOutlined,
} from "@ant-design/icons";

import { useTranslation } from "react-i18next";
import { QuizApi } from "../../../../api/quiz";
import { TeacherAPI } from "../../../../api/teacherAPI";
import { useAuth } from "../../../../hooks";
import { NEW_EVALUACIONES } from "../../../../ui/featureFlags";
import { BasicModal } from "../../../Shared";
import { QuizForm } from "../QuizForm/QuizForm";

import styles from "./ListQuiz.module.scss";

const { Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

function ListQuizInner(props) {
  const { reload, onReload } = props;

  const { t } = useTranslation("evaluations");
  const { accessToken, user } = useAuth();
  const teacherId = user?.user?.teacher;

  const quizApi = useMemo(() => new QuizApi(), []);
  const teacherApi = useMemo(() => new TeacherAPI(), []);

  const { message, modal } = AntApp.useApp();

  // listado quizzes
  const [loadingList, setLoadingList] = useState(false);
  const [quizzes, setQuizzes] = useState(null);

  // edición
  const [editOpen, setEditOpen] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);

  // ranking
  const [viewOpen, setViewOpen] = useState(false);
  const [classNumbers, setClassNumbers] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [selectedClass, setSelectedClass] = useState("");
  const [rankingData, setRankingData] = useState([]);
  const [loadingRanking, setLoadingRanking] = useState(false);

  // detalle alumno
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detailResults, setDetailResults] = useState(null);

  const screens = useBreakpoint();
  const isMdUp = Boolean(screens.md);

  // cargar quizzes
  useEffect(() => {
    let active = true;

    (async () => {
      try {
        setLoadingList(true);
        const result = await quizApi.getAllQuizzes(accessToken, teacherId);
        if (!active) return;
        setQuizzes(result?.quizzes || []);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error cargando quizzes:", err);
        if (!active) return;
        setQuizzes([]);
      } finally {
        if (active) setLoadingList(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [accessToken, teacherId, quizApi, reload]);

  // cargar clases del teacher (una vez)
  useEffect(() => {
    let active = true;

    (async () => {
      if (!accessToken || !teacherId) return;
      try {
        setLoadingClasses(true);
        const res = await teacherApi.getTeacherById(accessToken, teacherId);
        const roles = Array.isArray(res?.teacher?.classrole)
          ? res.teacher.classrole
          : [];
        const classes = roles.map((r) => r.classnumber).filter(Boolean);
        if (active) setClassNumbers(classes);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error cargando clases del teacher:", err);
        if (active) setClassNumbers([]);
      } finally {
        if (active) setLoadingClasses(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [accessToken, teacherApi, teacherId]);

  // ====== acciones ======
  const openEdit = useCallback((quiz) => {
    setSelectedQuiz(quiz);
    setEditOpen(true);
  }, []);

  const closeEdit = useCallback(() => {
    setEditOpen(false);
    setSelectedQuiz(null);
  }, []);

  const openView = useCallback((quiz) => {
    setSelectedQuiz(quiz);
    setSelectedClass("");
    setRankingData([]);
    setViewOpen(true);
  }, []);

  const closeView = useCallback(() => {
    setViewOpen(false);
    setSelectedClass("");
    setRankingData([]);
    setSelectedQuiz(null);
  }, []);

  const confirmDelete = useCallback(
    (quiz) => {
      const title = quiz?.name || t("quiz.noTitle");
      modal.confirm({
        title: t("quiz.deleteTitle"),
        content: t("quiz.deleteContent", { title }),
        okText: t("quiz.deleteConfirm"),
        cancelText: t("quiz.deleteCancel"),
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            await quizApi.deleteQuiz(accessToken, quiz._id);
            message.success(t("quiz.deleteSuccess"));
            if (typeof onReload === "function") onReload();
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error("Error eliminando quiz:", err);
            message.error(t("quiz.deleteError"));
          }
        },
      });
    },
    [accessToken, message, modal, onReload, quizApi, t],
  );

  const fetchRankingForClass = useCallback(
    async (classnumber) => {
      if (!selectedQuiz?._id) return;
      setSelectedClass(classnumber);
      setLoadingRanking(true);
      try {
        const res = await quizApi.getRankingResults(
          accessToken,
          teacherId,
          selectedQuiz._id,
          classnumber,
        );
        setRankingData(res?.ranking || []);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error obteniendo ranking:", err);
        setRankingData([]);
      } finally {
        setLoadingRanking(false);
      }
    },
    [accessToken, quizApi, selectedQuiz, teacherId],
  );

  const fetchStudentDetail = useCallback(
    async (studentId) => {
      if (!selectedQuiz?._id) return;
      setDetailLoading(true);
      setDetailError(null);

      try {
        const res = await quizApi.getDetailedStudentResult(
          accessToken,
          studentId,
          selectedQuiz._id,
        );
        const data = res?.result;

        const quizQuestions = Array.isArray(selectedQuiz?.questions)
          ? selectedQuiz.questions
          : [];

        const fullDetails = quizQuestions.map((q) => {
          const wrong = (data?.incorrectDetails || []).find(
            (d) => d.question === q.question,
          );
          const selected = wrong ? wrong.selected : q.correct;
          const correctAnswer = q.correct;
          const isCorrect = selected === correctAnswer;
          return { question: q.question, selected, correctAnswer, isCorrect };
        });

        setDetailResults({ timeTaken: data?.timeTaken, details: fullDetails });
        setDetailOpen(true);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error obteniendo detalle del alumno:", err);
        setDetailError(err?.message || "Error desconocido");
      } finally {
        setDetailLoading(false);
      }
    },
    [accessToken, quizApi, selectedQuiz],
  );

  const closeDetail = useCallback(() => {
    setDetailOpen(false);
    setDetailResults(null);
    setDetailError(null);
  }, []);

  // ====== columnas listado ======
  const columns = useMemo(() => {
    const getMenuItems = (record) => [
      {
        key: "edit",
        label: t("quiz.editLabel"),
        icon: <EditOutlined />,
      },
      {
        key: "view",
        label: t("quiz.viewRankingLabel"),
        icon: <EyeOutlined />,
      },
      {
        key: "delete",
        label: t("quiz.deleteLabel"),
        icon: <DeleteOutlined />,
        danger: true,
      },
    ];

    const handleMenuClick = (key, record) => {
      if (key === "edit") openEdit(record);
      if (key === "view") openView(record);
      if (key === "delete") confirmDelete(record);
    };

    return [
      {
        title: t("quiz.colTitle"),
        dataIndex: "name",
        key: "name",
        ellipsis: true,
        render: (_, record) => {
          const value = record?.name || record?.title || "";
          return (
            <Text
              strong
              title={value || t("quiz.noTitle")}
              className={styles.titleCell}
            >
              {value || t("quiz.noTitle")}
            </Text>
          );
        },
      },
      {
        title: t("quiz.colDescription"),
        dataIndex: "description",
        key: "description",
        responsive: ["md"],
        render: (value) =>
          value ? (
            <Paragraph
              className={styles.desc}
              ellipsis={{ rows: 2, tooltip: value }}
            >
              {value}
            </Paragraph>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: t("quiz.colQuestions"),
        dataIndex: "questions",
        key: "questions",
        width: 140,
        align: "center",
        render: (qs) => {
          const count = Array.isArray(qs) ? qs.length : Number(qs || 0);
          return (
            <Tag
              className={`${styles.countTag} ${
                NEW_EVALUACIONES ? styles.countTagGreen : ""
              }`}
              icon={<CheckCircleTwoTone twoToneColor="#00a75a" />}
            >
              {count}
            </Tag>
          );
        },
      },
      {
        title: t("quiz.actionsLabel"),
        key: "actions",
        width: isMdUp ? 210 : 86,
        align: "center",
        render: (_, record) => {
          if (!isMdUp) {
            return (
              <Dropdown
                trigger={["click"]}
                placement="bottomRight"
                menu={{
                  items: getMenuItems(record),
                  onClick: ({ key }) => handleMenuClick(key, record),
                }}
              >
                <Button
                  aria-label={t("quiz.actionsLabel")}
                  className={styles.iconBtn}
                  icon={<MoreOutlined />}
                />
              </Dropdown>
            );
          }

          if (NEW_EVALUACIONES) {
            return (
              <Flex gap={4} align="center" justify="center" wrap="wrap">
                <Tooltip title={t("quiz.editLabel")}>
                  <Button
                    type="text"
                    aria-label={t("quiz.editLabel")}
                    className={styles.actEdit}
                    icon={<EditOutlined />}
                    onClick={() => openEdit(record)}
                  />
                </Tooltip>

                <Tooltip title={t("quiz.viewRankingLabel")}>
                  <Button
                    type="text"
                    aria-label={t("quiz.viewRankingLabel")}
                    className={styles.actView}
                    icon={<EyeOutlined />}
                    onClick={() => openView(record)}
                  />
                </Tooltip>

                <Tooltip title={t("quiz.deleteLabel")}>
                  <Button
                    type="text"
                    danger
                    aria-label={t("quiz.deleteLabel")}
                    icon={<DeleteOutlined />}
                    onClick={() => confirmDelete(record)}
                  />
                </Tooltip>
              </Flex>
            );
          }

          return (
            <Flex gap={8} align="center" justify="center" wrap="wrap">
              <Tooltip title={t("quiz.editLabel")}>
                <Button
                  aria-label={t("quiz.editLabel")}
                  className={`${styles.iconBtn} ${styles.btnEdit}`}
                  icon={<EditOutlined />}
                  onClick={() => openEdit(record)}
                />
              </Tooltip>

              <Tooltip title={t("quiz.viewRankingLabel")}>
                <Button
                  aria-label={t("quiz.viewRankingLabel")}
                  className={`${styles.iconBtn} ${styles.btnView}`}
                  icon={<EyeOutlined />}
                  onClick={() => openView(record)}
                />
              </Tooltip>

              <Tooltip title={t("quiz.deleteLabel")}>
                <Button
                  aria-label={t("quiz.deleteLabel")}
                  className={`${styles.iconBtn} ${styles.btnDelete}`}
                  icon={<DeleteOutlined />}
                  onClick={() => confirmDelete(record)}
                />
              </Tooltip>
            </Flex>
          );
        },
      },
    ];
  }, [confirmDelete, isMdUp, openEdit, openView, t]);

  const expandable = useMemo(() => {
    if (isMdUp) return undefined;
    return {
      expandedRowRender: (record) =>
        record?.description ? (
          <div className={styles.expanded}>
            <Text strong>{t("quiz.colDescription")}</Text>
            <Paragraph className={styles.expandedDesc}>
              {record.description}
            </Paragraph>
          </div>
        ) : null,
      rowExpandable: (record) => Boolean(record?.description),
    };
  }, [isMdUp, t]);

  if (quizzes?.length === 0) {
    return (
      <div className={styles.emptyWrap}>
        <Empty
          description={t("quiz.noQuizzes")}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <>
      <div className={styles.wrap}>
        <div className={styles.table}>
          <Table
            rowKey={(record) => record?._id}
            loading={loadingList}
            dataSource={quizzes || []}
            columns={columns}
            tableLayout="fixed"
            sticky
            expandable={expandable}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50],
            }}
          />
        </div>
      </div>

      {/* ===== Modal Editar (como el legacy) ===== */}
      <BasicModal
        show={editOpen}
        close={closeEdit}
        title={
          selectedQuiz?.name
            ? `${t("quiz.editQuizTitle")}: ${selectedQuiz.name}`
            : t("quiz.editQuizTitle")
        }
        size="large"
      >
        <QuizForm quiz={selectedQuiz} onClose={closeEdit} onReload={onReload} />
      </BasicModal>

      {/* ===== Modal Ranking ===== */}
      <Modal
        open={viewOpen}
        onCancel={closeView}
        footer={null}
        width={900}
        destroyOnHidden
        title={
          selectedQuiz?.name
            ? `${t("quiz.rankingTitle")}: ${selectedQuiz.name}`
            : t("quiz.rankingTitle")
        }
      >
        {loadingClasses ? (
          <div className={styles.center}>
            <Spin />
          </div>
        ) : !selectedClass ? (
          <div className={styles.classButtons}>
            {classNumbers.length === 0 ? (
              <Text type="secondary">{t("quiz.noClasses")}</Text>
            ) : (
              classNumbers.map((cls) => (
                <Button
                  key={cls}
                  className={styles.classBtn}
                  onClick={() => fetchRankingForClass(cls)}
                >
                  {cls}
                </Button>
              ))
            )}
          </div>
        ) : loadingRanking ? (
          <div className={styles.center}>
            <Spin />
          </div>
        ) : (
          <>
            <div className={styles.rankingHeader}>
              <Text strong>{t("quiz.classLabel")}</Text>{" "}
              <Tag className={styles.classTag}>{selectedClass}</Tag>
              <Button
                onClick={() => setSelectedClass("")}
                className={styles.backBtn}
              >
                {t("quiz.changeClass")}
              </Button>
            </div>

            <Table
              rowKey={(r) => r.studentId}
              dataSource={rankingData || []}
              pagination={false}
              tableLayout="fixed"
              columns={[
                {
                  title: "#",
                  key: "pos",
                  width: 60,
                  render: (_, __, idx) => idx + 1,
                },
                {
                  title: t("quiz.colStudent"),
                  dataIndex: "studentName",
                  key: "studentName",
                  ellipsis: true,
                },
                {
                  title: t("quiz.colCorrect"),
                  dataIndex: "totalCorrect",
                  key: "totalCorrect",
                  width: 120,
                  align: "center",
                },
                {
                  title: t("quiz.colIncorrect"),
                  dataIndex: "totalIncorrect",
                  key: "totalIncorrect",
                  width: 120,
                  align: "center",
                },
                {
                  title: t("quiz.colTime"),
                  dataIndex: "timeTaken",
                  key: "timeTaken",
                  width: 120,
                  align: "center",
                },
                {
                  title: t("quiz.colDetail"),
                  key: "detail",
                  width: 110,
                  align: "center",
                  render: (_, r) => (
                    <Button
                      className={styles.iconBtn}
                      icon={<EyeOutlined />}
                      onClick={() => fetchStudentDetail(r.studentId)}
                    />
                  ),
                },
              ]}
            />
          </>
        )}
      </Modal>

      {/* ===== Modal Detalle Alumno ===== */}
      <Modal
        open={detailOpen}
        onCancel={closeDetail}
        footer={null}
        width={900}
        destroyOnHidden
        title={t("quiz.detailTitle")}
      >
        {detailLoading ? (
          <div className={styles.center}>
            <Spin />
          </div>
        ) : detailError ? (
          <Text type="danger">{detailError}</Text>
        ) : detailResults ? (
          <>
            <Paragraph style={{ marginBottom: 10 }}>
              <Text strong>{t("quiz.timeTaken")}</Text>{" "}
              {detailResults.timeTaken}s
            </Paragraph>

            <Table
              rowKey={(_, idx) => String(idx)}
              dataSource={detailResults.details}
              pagination={false}
              tableLayout="fixed"
              columns={[
                {
                  title: "#",
                  key: "idx",
                  width: 60,
                  render: (_, __, idx) => idx + 1,
                },
                {
                  title: t("quiz.colQuestion"),
                  dataIndex: "question",
                  key: "question",
                  ellipsis: true,
                },
                {
                  title: t("quiz.colAnswer"),
                  dataIndex: "selected",
                  key: "selected",
                  render: (val, row) => (
                    <div
                      className={
                        row.isCorrect ? styles.answerOk : styles.answerBad
                      }
                    >
                      {val}
                    </div>
                  ),
                },
                {
                  title: t("quiz.colCorrectAnswer"),
                  dataIndex: "correctAnswer",
                  key: "correctAnswer",
                },
              ]}
            />
          </>
        ) : null}
      </Modal>
    </>
  );
}

export function ListQuiz(props) {
  return (
    <AntApp>
      <ListQuizInner {...props} />
    </AntApp>
  );
}
